import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  TextField,
  useTheme,
  Grid,
  Fade,
  Slide,
} from '@mui/material';
import { keyframes } from '@mui/system';
import { motion } from 'framer-motion';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LandingPage = ({ 
  nickname, 
  setNickname, 
  error, 
  handleSubmit, 
  termsAccepted 
}) => {
  const theme = useTheme();

  const features = [
    {
      title: "Real-time Chat",
      description: "Instant messaging with seamless real-time communication",
      color: theme.palette.primary.main
    },
    {
      title: "Anonymous & Safe",
      description: "Chat without revealing personal information",
      color: theme.palette.secondary.main
    },
    {
      title: "Smart Pairing",
      description: "Get matched with new conversation partners instantly",
      color: theme.palette.info.main
    },
    {
      title: "Typing Indicators",
      description: "See when your chat partner is typing",
      color: theme.palette.success.main
    },
    {
      title: "Forever Free",
      description: "Completely free to use, supported by minimal ads to keep the service running",
      color: theme.palette.warning.main
    }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', py: { xs: 4, sm: 8 }, background: 'transparent'}}>
        <Fade in timeout={1000}>
          <Paper 
            elevation={3} 
            sx={{
              p: { xs: 3, sm: 6 },
              borderRadius: 4,
              background: theme.palette.background.paper,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Typography 
                  variant="h2" 
                  component="h1" 
                  gutterBottom
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: '2.5rem', sm: '3.75rem' },
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2
                  }}
                >
                  Someone2Talk
                </Typography>
              </motion.div>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4,
                  fontFamily: "'Poppins', sans-serif",
                  animation: `${fadeIn} 1s ease-out`,
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: { xs: '1.1rem', sm: '1.5rem' }
                }}
              >
                Connect with people around the world, anonymously and safely
              </Typography>
            </Box>

            <Grid container spacing={4} sx={{ mb: 6 }}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Slide 
                    direction={index % 2 === 0 ? "right" : "left"} 
                    in 
                    timeout={500 + (index * 200)}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        height: '100%',
                        background: theme.palette.grey[100],
                        borderRadius: 3,
                        transition: 'all 0.3s ease-in-out',
                        border: `1px solid ${theme.palette.grey[200]}`,
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                          borderColor: feature.color,
                        }
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ 
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 600,
                          color: feature.color
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography 
                        variant="body1"
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontFamily: "'Inter', sans-serif"
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Paper>
                  </Slide>
                </Grid>
              ))}
            </Grid>

            <Box 
              component="form" 
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                maxWidth: 400,
                mx: 'auto',
                animation: `${fadeIn} 1s ease-out 0.5s both`,
              }}
            >
              <TextField
                fullWidth
                label="Choose your nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                error={!!error}
                helperText={error}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: theme.palette.grey[100],
                    '&:hover': {
                      backgroundColor: theme.palette.grey[200],
                    },
                    '& fieldset': {
                      borderColor: theme.palette.grey[300],
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                  },
                }}
              />
              
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!termsAccepted}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  },
                  '&:disabled': {
                    background: theme.palette.grey[300],
                  }
                }}
              >
                Start Chatting
              </Button>
              
              {!termsAccepted && (
                <Typography 
                  variant="body2" 
                  color="error" 
                  align="center"
                  sx={{ 
                    fontFamily: "'Inter', sans-serif",
                    animation: `${fadeIn} 0.5s ease-out`
                  }}
                >
                  Please accept the Terms and Conditions to continue
                </Typography>
              )}
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default LandingPage; 